import { useEffect } from "react";

/**
 * Lightweight SEO meta manager. Mutates <title>, description, canonical
 * and og:/twitter: tags on mount; restores them on unmount so the
 * landing-page defaults come back when the user navigates away.
 *
 * Not SSR — Google renders JS, and this is good enough to seed the
 * meta stack for indexing. For richer needs, upgrade to react-helmet-async.
 */
export function useSeo({ title, description, canonical, image, type = "article" }) {
  useEffect(() => {
    const prev = { title: document.title, tags: {} };

    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const parts = selector.replace(/[[\]"']/g, "").split("=");
        el.setAttribute(parts[0].startsWith("meta") ? parts[0].slice(4) : parts[0], parts[1] || "");
        document.head.appendChild(el);
      }
      prev.tags[selector] = { el, value: el.getAttribute(attr), created: !document.head.contains(el) };
      el.setAttribute(attr, value);
    };

    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (link) {
        prev.canonical = link.getAttribute("href");
        link.setAttribute("href", canonical);
      }
      setMeta('meta[property="og:url"]', "content", canonical);
    }
    if (image) {
      setMeta('meta[property="og:image"]', "content", image);
      setMeta('meta[name="twitter:image"]', "content", image);
    }
    setMeta('meta[property="og:type"]', "content", type);

    return () => {
      document.title = prev.title;
      Object.values(prev.tags).forEach(({ el, value }) => {
        if (value != null) el.setAttribute("content", value);
      });
      if (prev.canonical) {
        const link = document.head.querySelector('link[rel="canonical"]');
        if (link) link.setAttribute("href", prev.canonical);
      }
    };
  }, [title, description, canonical, image, type]);
}
